import { Chart, ChartConfiguration } from 'chart.js';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilePdf, FaShareAlt } from 'react-icons/fa';
import { useAuthContext } from '../../contexts/auth.context';
import { Habit } from '../../dto/habit';
import { HabitFrequencyType, HabitTargetType, TargetDirection, TargetEvaluation } from '../../dto/habit.enum';
import { getTodaysRecord, Record } from '../../dto/record';
import { useHabit } from '../../hooks/habit.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useRecord } from '../../hooks/record.hook';
import {
  avg,
  daysAfter,
  daysBetween,
  getCutoffDate,
  getDay,
  getLocaleDate,
  getThemeColor,
  monthDaysBetween,
  monthsBetween,
  round,
  specialDaysBetween,
  TimeFrame,
  weekDaysBetween,
  weeksBetween,
} from '../../util/util';
import { generateReportPdf, ReportAction } from '../../util/report-pdf';

export default function ReportScreen(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user } = useAuthContext();
  const { habitsLoading, habits } = useHabit();
  const { recordsLoading, recordsByHabit } = useRecord();

  const [timeFrame, setTimeFrame] = useState<TimeFrame>(TimeFrame.MONTH);
  const [selectedHabitIds, setSelectedHabitIds] = useState<Set<string>>(new Set());
  const [pendingAction, setPendingAction] = useState<ReportAction>();

  const activeHabits = useMemo(() => habits.filter((h) => !h.end), [habits]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayout(
    () => ({
      title: t('Report'),
      backButton: true,
      loading: habitsLoading || recordsLoading,
    }),
    [t, habitsLoading, recordsLoading],
  );

  // default: all active habits selected
  useEffect(() => {
    if (activeHabits.length === 0) return;
    setSelectedHabitIds(new Set(activeHabits.map((h) => h.id)));
  }, [activeHabits]);

  function toggleHabit(id: string) {
    setSelectedHabitIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedHabitIds.size === activeHabits.length) setSelectedHabitIds(new Set());
    else setSelectedHabitIds(new Set(activeHabits.map((h) => h.id)));
  }

  const selectedHabits = useMemo(
    () => activeHabits.filter((h) => selectedHabitIds.has(h.id)),
    [activeHabits, selectedHabitIds],
  );

  async function handleExport(action: ReportAction) {
    if (!user || selectedHabits.length === 0) return;

    setPendingAction(action);
    try {
      const sections = selectedHabits.map((habit) =>
        buildSection(t, habit, recordsByHabit[habit.id] ?? [], timeFrame),
      );

      const rangeStart = getDay(getCutoffDate(timeFrame));
      const rangeEnd = getDay(new Date());
      const periodRange =
        timeFrame === TimeFrame.ALL ? t(timeFrame) : `${formatFullDate(rangeStart)} – ${formatFullDate(rangeEnd)}`;

      await generateReportPdf({
        userName: user.name,
        timeFrame: t(timeFrame),
        periodRange,
        generatedAt: formatFullDate(new Date()),
        language: i18n.language,
        labels: {
          reportTitle: t('Virtus Report'),
          target: t('Target'),
          outcome: t('Outcome'),
          score: t('Score'),
          progress: t('Progress'),
          average: t('Average'),
          period: t('Period'),
          noRecords: t('No records in this timeframe'),
        },
        sections,
        action,
      });
    } catch (err) {
      console.error('PDF export failed', err);
    } finally {
      setPendingAction(undefined);
    }
  }

  if (activeHabits.length === 0) {
    return (
      <div className="text-center text-base-content/70">{t('Start tracking a habit to view your statistics!')}</div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-semibold mb-2">{t('Timeframe')}</h2>
        <select
          className="select select-lg w-full"
          value={timeFrame}
          onChange={(e) => setTimeFrame(e.target.value as TimeFrame)}
        >
          {Object.values(TimeFrame).map((tf) => (
            <option key={tf} value={tf}>
              {t(tf)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">{t('Habits')}</h2>
          <button className="btn btn-ghost btn-sm" onClick={toggleAll}>
            {selectedHabitIds.size === activeHabits.length ? t('Deselect all') : t('Select all')}
          </button>
        </div>
        <div className="flex flex-col gap-2 bg-base-100 rounded-lg p-3">
          {activeHabits.map((h) => (
            <label key={h.id} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox"
                checked={selectedHabitIds.has(h.id)}
                onChange={() => toggleHabit(h.id)}
              />
              <span>{t(h.title)}</span>
            </label>
          ))}
        </div>
      </div>

      <button
        className="btn btn-primary btn-lg w-full"
        onClick={() => handleExport('open')}
        disabled={selectedHabits.length === 0 || pendingAction != null}
      >
        {pendingAction === 'open' ? <span className="loading loading-spinner loading-sm" /> : <FaFilePdf />}
        {t('Open PDF')}
      </button>
      <button
        className="btn btn-neutral btn-lg w-full"
        onClick={() => handleExport('share')}
        disabled={selectedHabits.length === 0 || pendingAction != null}
      >
        {pendingAction === 'share' ? <span className="loading loading-spinner loading-sm" /> : <FaShareAlt />}
        {t('Share')}
      </button>
    </div>
  );
}

interface ReportSection {
  title: string;
  notes?: string;
  targetLabel?: string;
  outcomeLabel?: string;
  scoreLabel?: string;
  scoreValue?: string;
  averageText?: string;
  periodText: string;
  chartImage?: string;
  hasRecords: boolean;
}

function buildSection(
  t: (key: string) => string,
  habit: Habit,
  allRecords: Record[],
  timeFrame: TimeFrame,
): ReportSection {
  const cutoffDate = getDay(getCutoffDate(timeFrame));
  const startDate = habit.start > cutoffDate ? habit.start : cutoffDate;
  const currentRecord = getTodaysRecord(allRecords);
  const endDate = habit.end ?? daysAfter(currentRecord ? 1 : 0, getDay(new Date()));
  const dayCount = Math.round(daysBetween(startDate, endDate));
  const records = allRecords.filter((r) => r.date >= startDate);

  const periodText = `${getLocaleDate(startDate)} – ${getLocaleDate(endDate)}`;
  const notes = habit.notes ? t(habit.notes) : undefined;

  if (records.length === 0) {
    return {
      title: t(habit.title),
      notes,
      periodText,
      hasRecords: false,
    };
  }

  const isBoolean = habit.target.type === HabitTargetType.BOOLEAN;
  const isQuantity = habit.target.type === HabitTargetType.QUANTITY;

  let target: number;
  let outcome: number;

  if (isBoolean) {
    switch (habit.frequency.type) {
      case HabitFrequencyType.DAILY:
        target = dayCount;
        break;
      case HabitFrequencyType.WEEKLY:
        target = round(habit.frequency.count * weeksBetween(startDate, endDate), 1);
        break;
      case HabitFrequencyType.MONTHLY:
        target = round(habit.frequency.count * monthsBetween(startDate, endDate), 1);
        break;
      case HabitFrequencyType.WEEK_DAYS:
        target = weekDaysBetween(habit.frequency.weekDays ?? [], startDate, endDate);
        break;
      case HabitFrequencyType.MONTH_DAYS:
        target = monthDaysBetween(habit.frequency.monthDays ?? [], startDate, endDate);
        break;
      case HabitFrequencyType.SPECIAL_DAYS:
        target = habit.frequency.specialDays
          ? specialDaysBetween(habit.frequency.specialDays, startDate, endDate)
          : 0;
        break;
      default:
        target = 0;
    }
    outcome = records.filter((r) => r.achievement >= r.target).length;
  } else {
    const averagingRecords = habit.target.evaluation === TargetEvaluation.FINAL ? records.slice(-3) : records;
    target = round(avg(averagingRecords.map((r) => r.target)), 1);
    outcome = round(avg(averagingRecords.map((r) => r.achievement)), 1);
  }

  const score =
    habit.target.direction === TargetDirection.REDUCTION
      ? target
        ? Math.round(100 * (target / outcome))
        : -outcome + 100
      : target
        ? Math.round((100 * outcome) / target)
        : outcome + 100;

  const chartImage = renderChartImage(t, habit, records, startDate, dayCount);

  const scoreLabel = habit.target.evaluation === TargetEvaluation.FINAL ? t('Progress') : t('Score');
  const scoreValue = score === Infinity ? '∞' : `${score}%`;

  if (isBoolean || isQuantity) {
    return {
      title: t(habit.title),
      notes,
      periodText,
      hasRecords: true,
      chartImage,
      targetLabel: String(target),
      outcomeLabel: String(outcome),
      scoreLabel,
      scoreValue,
    };
  }

  // rating
  return {
    title: t(habit.title),
    notes,
    periodText,
    hasRecords: true,
    chartImage,
    averageText: `${outcome} / ${target}`,
  };
}

function renderChartImage(
  t: (key: string) => string,
  habit: Habit,
  records: Record[],
  startDate: Date,
  dayCount: number,
): string | undefined {
  // build chart values including empty days (matches statistic.tsx)
  const values: { date: Date; value?: number; target?: number }[] = records.map((r) => ({
    date: r.date,
    value: r.achievement,
    target: habit.target.type === HabitTargetType.QUANTITY ? r.target : undefined,
  }));

  if (values.length > 0) {
    const firstEntry = values.at(0);
    const lastEntry = values.at(-1);
    const existingDates = new Set(values.map((v) => v.date.toDateString()));

    for (let i = 0; i < dayCount; i++) {
      const date = daysAfter(i, startDate);
      if (existingDates.has(date.toDateString())) continue;
      const target =
        firstEntry && date < firstEntry.date
          ? firstEntry.target
          : lastEntry && date > lastEntry.date
            ? lastEntry.target
            : undefined;
      values.push(
        habit.target.type === HabitTargetType.BOOLEAN ? { date, value: 0, target: 1 } : { date, target },
      );
    }
    values.sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // white background so PDF looks clean regardless of theme
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const primary = getThemeColor('--color-primary') || '#5a4a3a';
  const secondary = getThemeColor('--color-secondary') || '#a08a72';
  const success = getThemeColor('--color-success') || '#4caf50';
  const error = getThemeColor('--color-error') || '#e57373';

  const labels = values.map((v) => formatFullDate(v.date));

  let config: ChartConfiguration;

  if (habit.target.type === HabitTargetType.BOOLEAN) {
    config = {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            data: values.map((v) => v.value ?? 0),
            backgroundColor: values.map((v) => ((v.value ?? 0) > 0 ? success : error)),
          },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        devicePixelRatio: 2,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 10, color: '#333' } },
          y: {
            min: -1,
            max: 1,
            ticks: {
              stepSize: 1,
              callback: (v) => (+v > 0 ? '✔' : +v < 0 ? '✘' : ''),
              color: '#333',
            },
          },
        },
      },
    };
  } else {
    const hasTargets = values.some((v) => v.target != null);
    config = {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: t('Outcome'),
            data: values.map((v) => v.value ?? null),
            borderColor: primary,
            backgroundColor: primary,
            tension: 0.4,
            spanGaps: true,
            pointRadius: 3,
          },
          ...(hasTargets
            ? [
                {
                  label: t('Target'),
                  data: values.map((v) => v.target ?? null),
                  borderColor: secondary,
                  backgroundColor: secondary,
                  tension: 0.5,
                  spanGaps: true,
                  pointRadius: 0,
                },
              ]
            : []),
        ],
      },
      options: {
        responsive: false,
        animation: false,
        devicePixelRatio: 2,
        plugins: { legend: { display: hasTargets, labels: { color: '#333' } } },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 10, color: '#333' } },
          y: { ticks: { color: '#333' } },
        },
      },
    };

    if (habit.target.type === HabitTargetType.RATING) {
      config.options!.scales!.y = { ...config.options!.scales!.y, min: 1, max: 10 };
    }
  }

  const chart = new Chart(ctx, config);
  const image = canvas.toDataURL('image/png');
  chart.destroy();
  return image;
}

function formatFullDate(date: Date): string {
  const userLocale = navigator.language || 'en-US';
  return new Intl.DateTimeFormat(userLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export type { ReportSection };
